import { dmyToYmd } from './reportDateParams'
import type { MrgReportListPatch } from '../types/utils'
import { MRG_REPORT_SESSION_KEY } from '../constants/sessionKeys'

export { MRG_REPORT_SESSION_KEY }

export function readMrgReportSession(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(MRG_REPORT_SESSION_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as unknown
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function writeMrgReportSession(data: Record<string, unknown>): void {
  sessionStorage.setItem(MRG_REPORT_SESSION_KEY, JSON.stringify(data))
}

export function clearMrgReportSession(): void {
  sessionStorage.removeItem(MRG_REPORT_SESSION_KEY)
}

function normalizeStoredDate(value: unknown): string {
  if (value == null) return ''
  const s = String(value).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  return dmyToYmd(s) || s
}

export function sessionFilterToMrgReportPatch(session: Record<string, unknown>): MrgReportListPatch {
  const patch: MrgReportListPatch = {}
  if (typeof session.page === 'number' && session.page > 0) patch.page = session.page
  if (typeof session.perPage === 'number' && session.perPage > 0) patch.perPage = session.perPage
  if (session.sortBy === 'asc' || session.sortBy === 'desc') patch.sortBy = session.sortBy
  if (typeof session.orderBy === 'string' && session.orderBy.trim()) patch.orderBy = session.orderBy
  if (typeof session.q === 'string') patch.q = session.q
  if (session.company_id != null && session.company_id !== '') {
    patch.company_id = String(session.company_id)
  }
  if (typeof session.company_name === 'string') patch.company_name = session.company_name
  if (session.property_id != null && session.property_id !== '') {
    patch.property_id = String(session.property_id)
  }
  if (typeof session.property_name === 'string') patch.property_name = session.property_name
  const fromDt = normalizeStoredDate(session.fromDt)
  const toDt = normalizeStoredDate(session.toDt)
  if (fromDt) patch.fromDt = fromDt
  if (toDt) patch.toDt = toDt
  const claimfromDt = normalizeStoredDate(session.claimfromDt)
  const claimtoDt = normalizeStoredDate(session.claimtoDt)
  if (claimfromDt) patch.claimfromDt = claimfromDt
  if (claimtoDt) patch.claimtoDt = claimtoDt
  if (Array.isArray(session.rentalStatus)) {
    patch.rentalStatus = session.rentalStatus.map(String)
  } else if (typeof session.rentalStatus === 'string' && session.rentalStatus) {
    patch.rentalStatus = [session.rentalStatus]
  }
  return patch
}

function ymdToDmy(ymd: string): string {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
  const [y, m, d] = ymd.split('-')
  return `${d}-${m}-${y}`
}

export function buildMrgReportSessionFromParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  q: string
  company_id: string
  company_name: string
  property_id: string
  property_name: string
  fromDt: string
  toDt: string
  claimfromDt: string
  claimtoDt: string
  rentalStatus: string[]
}): Record<string, unknown> {
  const out: Record<string, unknown> = {
    page: p.page,
    perPage: p.perPage,
    sortBy: p.sortBy,
    orderBy: p.orderBy,
    q: p.q,
    rentalStatus: p.rentalStatus,
  }
  if (p.company_id) {
    out.company_id = p.company_id
    if (p.company_name) out.company_name = p.company_name
  }
  if (p.property_id) {
    out.property_id = p.property_id
    if (p.property_name) out.property_name = p.property_name
  }
  const fd = ymdToDmy(p.fromDt)
  const td = ymdToDmy(p.toDt)
  if (fd) out.fromDt = fd
  if (td) out.toDt = td
  const cfd = ymdToDmy(p.claimfromDt)
  const ctd = ymdToDmy(p.claimtoDt)
  if (cfd) out.claimfromDt = cfd
  if (ctd) out.claimtoDt = ctd
  return out
}
